import { useState, useMemo } from 'react';
import { Search, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { InternalUserItem } from '@uniticket/shared';

interface BulkAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  internalUsers: InternalUserItem[];
  onConfirm: (userId: string | null) => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function BulkAssignDialog({ open, onOpenChange, selectedCount, internalUsers, onConfirm }: BulkAssignDialogProps) {
  const { t } = useTranslation('tickets');
  const [selected, setSelected] = useState<string | null | undefined>(undefined);
  const [search, setSearch] = useState('');

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return internalUsers;
    const q = search.toLowerCase();
    return internalUsers.filter(
      (u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [internalUsers, search]);

  function handleOpenChange(value: boolean) {
    if (!value) {
      setSelected(undefined);
      setSearch('');
    }
    onOpenChange(value);
  }

  function handleApply() {
    if (selected === undefined) return;
    onConfirm(selected);
    setSelected(undefined);
    setSearch('');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('bulk.assignTitle')}</DialogTitle>
          <DialogDescription>
            {t('bulk.assignDesc', { count: selectedCount })}
          </DialogDescription>
        </DialogHeader>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.searchUsers')}
            className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400"
          />
        </div>

        {/* User list */}
        <div className="max-h-64 overflow-y-auto space-y-1 py-1">
          {/* Unassign option */}
          <button
            onClick={() => setSelected(null)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-colors text-left ${
              selected === null
                ? 'border-brand-rose bg-brand-rose/5'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
              <UserX className="w-4 h-4 text-slate-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">{t('bulk.unassign')}</p>
              <p className="text-xs text-slate-400">{t('bulk.removeAssignee')}</p>
            </div>
          </button>

          {filteredUsers.map((user) => (
            <button
              key={user.id}
              onClick={() => setSelected(user.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-colors text-left ${
                selected === user.id
                  ? 'border-brand-rose bg-brand-rose/5'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                {getInitials(user.displayName)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{user.displayName}</p>
                <p className="text-xs text-slate-400 truncate">{user.email}</p>
              </div>
            </button>
          ))}

          {filteredUsers.length === 0 && search.trim() && (
            <p className="text-sm text-slate-400 text-center py-4">{t('bulk.noUsersFound')}</p>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={() => handleOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            onClick={handleApply}
            disabled={selected === undefined}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-brand-rose to-brand-orange hover:shadow-md"
          >
            {t('common:actions.apply')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
